import { FC, useContext, useEffect, useRef, useState } from "react";
import NotificationDispatch, { showErrorNotification } from "../../../../../context/notificationContext";
import { useSearchContext } from "../../../../../context/searchContext";
import { useDebounce } from "../../../../../hooks/useDebounce";
import { useEntityAccesses } from "../../../../../hooks/useEntityAccesses";
import { useFilterSearch } from "../../../../../hooks/useFilterSearch";
import useListFilters from "../../../../../hooks/useListFilters";
import useI18n from "../../../../../hooks/useTranslations";
import { HrailScrew } from "../../../../../http/hrail/types/screws";
import { useHrailScrewsApi } from "../../../../../http/hrail/useScrews";
import { AdminItem } from "../../../../../http/types/items";
import { PaginationQuery } from "../../../../../types";
import FlexContent from "../../../../ui/FlexContent";
import InlineOrderEditor from "../../../../ui/InlineOrderEditor";
import { getDeleteAction, getEditAction } from "../../../../ui/ListItemMenu";
import ModalDialog from "../../../../ui/ModalDialog";
import SearchBar from "../../../../ui/SearchBar";
import Table from "../../../../ui/Table";
import Visibility from "../../../../ui/Visibility";
import { VisibilityScopeValue } from "../../../../ui/VisibilityScope";
import CreateScrewModalDialogButton from "./CreateScrewModalDialogButton";
import ScrewForm from "./ScrewForm";

type Props = {
  active?: boolean;
};

const Screws: FC<Props> = ({ active }) => {
  const i18n = useI18n();
  const dispatch = useContext(NotificationDispatch);
  const filters = useListFilters({ key: "screws" });
  const aborter = useRef(new AbortController());
  const screwApi = useHrailScrewsApi();
  const [itemToEdit, setItemToEdit] = useState<HrailScrew>();
  const [itemToDelete, setItemToDelete] = useState<HrailScrew>();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { setActions, setTitle } = useSearchContext();
  const { searchTerm, handleSearchChange } = useFilterSearch(filters, aborter, "screws");
  const isActiveTab = active ?? true;
  const updateVisibility = async (screw: HrailScrew, visibility: VisibilityScopeValue) => {
    try {
      await screwApi.updateAdminHrailScrewVisibility(screw.item.id, { visibility });
    } catch (err) {
      dispatch(showErrorNotification(i18n, err));
    }
  };

  const updateOrder = async (screw: HrailScrew, newOrder: number) => {
    try {
      await screwApi.updateAdminHrailScrewOrder(screw.item.id, { order: newOrder });
    } catch (err) {
      dispatch(showErrorNotification(i18n, err));
    }
  };

  const { accessesAction, accessesDialog } = useEntityAccesses<AdminItem>("hrail.screw");

  const debouncedValue = useDebounce(searchTerm);

  const query: PaginationQuery = {
    ...filters.state,
    q: debouncedValue,
  };

  const { error, data } = screwApi.useAdminHrailScrews(query, aborter.current.signal);

  useEffect(() => {
    if (!isActiveTab) return;

    setTitle(i18n.translation.screws.plural);
    setActions(
      <>
        <SearchBar placeholder={i18n.translation.screws.search} value={searchTerm} onChange={handleSearchChange} />
        <CreateScrewModalDialogButton />
      </>,
    );

    return () => {
      setTitle(undefined);
      setActions(null);
    };
  }, [isActiveTab, searchTerm]);

  return (
    <FlexContent padding>
      <Table<HrailScrew>
        isTall={true}
        filters={filters}
        head={
          <tr>
            <th align="left">{i18n.translation.items.singular}</th>
            <th align="left">{i18n.translation.common.description}</th>
            <th>{i18n.translation.common.visibility}</th>
            <th>{i18n.translation.common.order}</th>
            <th></th>
          </tr>
        }
        items={data?.items}
        renderRow={(screw) => (
          <>
            <Table.Td noWrap={true}>{screw.item?.code ?? ""}</Table.Td>
            <Table.Td noWrap={false}>{screw.item?.description ?? ""}</Table.Td>
            <td align="center">
              <Visibility value={screw.visibility} onChange={(value) => updateVisibility(screw, value)} />
            </td>
            <td align="center">
              <InlineOrderEditor order={screw.order} onUpdate={(newOrder) => updateOrder(screw, newOrder)} />
            </td>
          </>
        )}
        onRowClick={(screw) => setItemToEdit(screw)}
        rowActions={(screw) => [
          getEditAction(i18n, () => setItemToEdit(screw)),
          accessesAction(screw.item),
          getDeleteAction(i18n, () => setItemToDelete(screw), true),
        ]}
        total={data?.total}
        error={!!error}
      />
      {accessesDialog}

      {!!itemToEdit && (
        <ModalDialog onClose={() => setItemToEdit(undefined)} title={i18n.translation.screws.edit}>
          <ScrewForm screw={itemToEdit} onClose={() => setItemToEdit(undefined)} />
        </ModalDialog>
      )}

      {!!itemToDelete && (
        <ModalDialog
          onClose={() => setItemToDelete(undefined)}
          title={i18n.translation.screws.delete}
          isSubmitting={isSubmitting}
          actions={[
            {
              title: i18n.translation.common.cancel,
              onClick: () => setItemToDelete(undefined),
              type: "secondary",
            },
            {
              title: i18n.translation.common.yes,
              onClick: () => {
                setIsSubmitting(true);
                screwApi
                  .deleteAdminHrailScrew(itemToDelete.item.id)
                  .then(() => setItemToDelete(undefined))
                  .catch((err) => dispatch(showErrorNotification(i18n, err)))
                  .finally(() => setIsSubmitting(false));
              },
            },
          ]}
        >
          {i18n.translation.common.deleteEntityQuestion(
            i18n.translation.screws.deleteLabel,
            [itemToDelete.item?.code ?? "", itemToDelete.item?.description ?? ""].filter(Boolean).join(" - ") ||
              itemToDelete.item.id,
          )}
        </ModalDialog>
      )}
    </FlexContent>
  );
};

export default Screws;
